import * as cornerstone from "@cornerstonejs/core";
import {
  Enums,
  RenderingEngine,
  getRenderingEngine,
} from "@cornerstonejs/core";
import { loadAndCacheImage } from "@cornerstonejs/core/loaders/imageLoader";
import cornerstoneDICOMImageLoader from "@cornerstonejs/dicom-image-loader";
import * as cornerstonetools from "@cornerstonejs/tools";
import { MouseBindings } from "@cornerstonejs/tools/enums";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "../../constants/Constants";
import {
  createVTKImageData,
  updateVTKImageDataRGBA,
} from "../StudyViewer/helpers/CornerstoneViewportService";
import hardcodedMetaDataProvider from "../StudyViewer/helpers/MetadataProvider";
import { metaData } from "@cornerstonejs/core";
import "./style.css";
import NearMouseTooltip from "./helpers/NearMouseTooltip";
import AugmentoIcon from "./SVGComponents/AugmentoIcon";
import DownArrowIcon from "./DownArrowIcon";
import ImageScrollbar from "./Components/Scrollbar/ImageScrollbar";
import SCSeriesPanel from "./SCSeriesPanel";

const renderingEngineId = "ai-rendering-engine";
const viewportId = "ai-viewport";
const toolGroupId = "widget-default";
// ✓ Global metadata provider registered in Cornerstone initialization
const { Events } = cornerstonetools.Enums; // ✅ optional shortcut

const viewportOverlayStyle1 = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "194px", // 👈 match viewport exactly
  height: "208px",
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2,
  borderRadius: "2px",
};

const viewportOverlayStyle2 = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "194px", // 👈 match viewport exactly
  height: "208px",
  display: "flex",
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2,
  borderRadius: "2px",
};

const headerStyle = {
  width: '100%',
  textAlign: 'center',
  color: '#F5F5F5',
  fontSize: 14,
  fontFamily: 'Open Sans',
  fontWeight: '600',
  paddingTop: '5px',
};

const findingsHeaderStyle = {

  color: '#F5F5F5',
  fontSize: 12,
  fontFamily: 'Open Sans',
  fontWeight: '700',
  // margin: '12px 12px 0px',
  margin: '5px  12px 0px 12px',
  borderBottom: '1px #F5F5F5 solid',
  // paddingTop:1
};

const findingStyle = {
  display: 'flex',
  alignItems: 'center',
  borderBottom: "2px groove rgba(255, 255, 255, 0.15)",
  paddingLeft: '5px',
  color: '#F5F5F5',
  fontSize: 10,
  fontFamily: 'Open Sans',
  fontWeight: 600,
  lineHeight: '1.5rem',
};

const dotStyle = {
  width: '3px',
  height: '3px',
  borderRadius: '50%',
  backgroundColor: '#F5F5F5',
  marginRight: '5px',
};

const ProgressBar = ({ loadingProgress }) => (
  <>
    <div style={{
      width: "80%",
      height: "8px",
      background: "#444",
      borderRadius: "4px",
      overflow: "hidden",
    }}>
      <div style={{
        width: `${loadingProgress}%`,
        height: "100%",
        backgroundColor: "#00e0ff",
        transition: "width 0.3s ease",
      }} />
    </div>
    <p style={{ color: "#fff", marginTop: "10px" }}>{loadingProgress}%</p>
  </>
);

const AIAnnotatedImage = ({
  images,
  isChestXray,
  studyId,
  firmId,
  userName,
  findings,
  showProbability,
  findingsAvailable,
  setShowFindings,
  aiModel,
  patientName,
  patientSex,
  patientAge,
  aiOutput,
  isAugDemo,
  augDemoStudyId,
  augDemoFirmId,
  augDemoUsername,
  scSeries = [] // <-- pass scSeries prop from parent
}) => {

  console.log("AIAnnotatedImage component rendered with images:", images);
  const imageIds = images
    ?.filter(img => {
      if (isChestXray) return true;
      return img.isAiOutput === true;
    })
    .map(img => {
      const storageKey = isChestXray ? img.thumbnailStorageKey : img.imageStorageKey;
      return `dicomweb:${API_BASE_URL}jwtServices/study/imageWithFileSize/get/${img.id}/${storageKey}`;
    }) || [];

console.log("aiOutput:", aiOutput);
const processedFindings = isChestXray
  ? findings
  : aiOutput
    ? aiOutput.split(',').map(item => {
        const [name, prob] = item.split('-');
        return {
          pathologyName: name.trim(),
          probabilityDisplay: prob?.trim() || '—',
        };
      })
    : [];

  console.log("Processed findings:", processedFindings);

  const elementRef = useRef(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [totalImages, setTotalImages] = useState(1);
  const wheelHandlerRef = useRef(null);
  const [viewport, setViewport] = useState(null);
  const [height, setHeight] = useState(0);
  const [scPanelCollapsed, setScPanelCollapsed] = useState(true);
  // Handler to load SC series into viewport
  const handleSelectSCSeries = async (series) => {
    if (!series || !series.imageList || !viewport) return;
    const imageIds = series.imageList
      .filter(img => img.imageStorageKey)
      .map(img => `dicomweb:${API_BASE_URL}jwtServices/study/imageWithFileSize/get/${img.id}/${img.imageStorageKey}`);
    setIsLoading(true);
    await viewport.setStack(imageIds, 0);
    viewport.render();
    setCurrentImageIndex(0);
    setIsLoading(false);
  };

  useEffect(() => {
    const init = async () => {
      if (!elementRef.current) return;
      let maxWebWorkers = 1;
      if (navigator.hardwareConcurrency) {
        maxWebWorkers = Math.min(navigator.hardwareConcurrency, 7);
      }
      cornerstoneDICOMImageLoader.init({
        maxWebWorkers: maxWebWorkers,
        startWebWorkersOnDemand: false,
        beforeSend: function (xhr) {
          xhr.withCredentials = true;
        },
      });
      cornerstone.init();
      cornerstonetools.init(); // Initialize before anything else
      // Destroy any prior engine with this id before creating a new one —
      // RenderingEngine uses a fixed id, so re-running init() (e.g. on images
      // change) would otherwise orphan the previous engine's WebGL context and
      // leak it, contributing to the browser's per-process context-limit
      // exhaustion when many studies/widgets are open.
      const existingEngine = getRenderingEngine(renderingEngineId);
      if (existingEngine) {
        try { existingEngine.destroy(); } catch (_) {}
      }
      const renderingEngine = new RenderingEngine(renderingEngineId);
      const viewportInput = {
        viewportId,
        type: Enums.ViewportType.STACK,
        element: elementRef.current,
        defaultOptions: { background: [0, 0, 0] },
      };
      renderingEngine.enableElement(viewportInput);
      const toolGroupId = "widget-default";
      const viewport = renderingEngine.getViewport(viewportId);
      let toolGroupManager = cornerstonetools.ToolGroupManager.getToolGroup(toolGroupId);
      if (!toolGroupManager) {
        toolGroupManager = cornerstonetools.ToolGroupManager.createToolGroup(toolGroupId);
      }
      toolGroupManager.addViewport(viewportId, renderingEngineId);
      console.log("imageIds:", imageIds);
      await viewport.setStack(imageIds, currentImageIndex);
      cornerstonetools.utilities.stackPrefetch.enable(viewport.element);
      viewport.render();
      setViewport(viewport);
      cornerstonetools.addTool(cornerstonetools.StackScrollTool);
      toolGroupManager.addTool(cornerstonetools.StackScrollTool.toolName, { loop: false });
      toolGroupManager.setToolActive(cornerstonetools.StackScrollTool.toolName, {
        bindings: [{ mouseButton: MouseBindings.Wheel }],
      });

      toolGroupManager.setToolPassive(cornerstonetools.StackScrollTool.toolName);
      const currentImage = images[currentImageIndex];

      if (isChestXray && currentImage?.heatmapStorageKey) {
        const heatmapImageId = `${API_BASE_URL}jwtServices/study/imageWithFileSize/get/${currentImage.id}/${currentImage.heatmapStorageKey}`;
        await loadOverlay(viewport, heatmapImageId, "heatmap", 0.4);
      }


      if (isChestXray && currentImage?.outlineStorageKey) {
        const outlineImageId = `${API_BASE_URL}jwtServices/study/imageWithFileSize/get/${currentImage.id}/${currentImage.outlineStorageKey}`;
        await loadOverlay(viewport, outlineImageId, "outline", 0.4);
      }
      setIsLoading(false);


    };

    init();

    // Release the WebGL context on unmount (and before each re-init) so the
    // AI widget doesn't permanently hold a context — important when several
    // studies are open and the browser's WebGL context budget is tight.
    return () => {
      const engine = getRenderingEngine(renderingEngineId);
      if (engine) {
        try { engine.destroy(); } catch (_) {}
      }
    };
  }, [images]);
  
  useEffect(() => {
    if (elementRef.current) {
      setHeight(elementRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    if (!elementRef.current || !viewport) return;

    const imageRenderedHandler = (evt) => {
      // Only handle events from the main viewport, not from thumbnails
      if (evt.target !== elementRef.current) {
        return;
      }

      // Get the current image index from the viewport
      const renderingEngine = getRenderingEngine(renderingEngineId);
      if (!renderingEngine) return;
      
      const vp = renderingEngine.getViewport(viewportId);
      if (!vp) return;

      const imageIdIndex = vp.getCurrentImageIdIndex();
      if (imageIdIndex !== undefined && imageIdIndex !== currentImageIndex) {
        console.log("Image rendered at index:", imageIdIndex);
        setCurrentImageIndex(imageIdIndex);
      }
    };

    const element = elementRef.current;

    element.addEventListener(
      cornerstone.Enums.Events.IMAGE_RENDERED,
      imageRenderedHandler
    );

    return () => {
      element.removeEventListener(
        cornerstone.Enums.Events.IMAGE_RENDERED,
        imageRenderedHandler
      );
    };
  }, [viewport, currentImageIndex]);

  useEffect(() => {
    if (elementRef.current) {
      setHeight(elementRef.current.offsetHeight);
    }
  }, []);

  const handleOpenViewerWindow = async (studyId, firmId, userName, isReportClicked, isAugDemo, isProductivityClicked, chartMode) => {
    try {
      let response = null;
      if (isAugDemo) {
        response = await fetch(`https://augmento-demo.deeptek.ai:8002/gizmo/generate-access-code`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ studyId, firmId, isReportClicked, isProductivityClicked, chartMode }),
        });
        }
        else{
          response = await fetch(`${API_BASE_URL}jwtServices/gizmo/generate-access-code`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ studyId, firmId, isReportClicked, isProductivityClicked, chartMode }),
        });
      }

      if (!response.ok) {
        const error = await response.text();
        console.error("Failed to generate access code:", error);
        alert("Unable to open viewer.");
        return;
      }

      const data = await response.json();
      const accessCode = data.accessCode;
      let viewerUrl = null;
      if (isAugDemo) {
        viewerUrl = `https://augmento-demo.deeptek.ai/gizmo-viewer?accessCode=${encodeURIComponent(accessCode)}&userName=${encodeURIComponent(userName)}`;
      } else {
        viewerUrl = `${window.location.origin}/gizmo-viewer?accessCode=${encodeURIComponent(accessCode)}&userName=${encodeURIComponent(userName)}`;
      }
      window.parent.postMessage({ type: "open-external", url: viewerUrl }, "*");
    } catch (err) {
      console.error("Error in handleOpenViewerWindow:", err);
      alert("Something went wrong while opening the viewer.");
    }
  };
 
  const loadOverlay = async (viewport, imageId, uid, opacity) => {
    if (!imageId) return;

    if (viewport.getActor(uid)) {
      viewport.removeActors([uid]);
    }

    const loadedImageData = viewport.getImageData();
    if (!loadedImageData) return;

    const imageBaseURL = imageId.startsWith("https")
      ? imageId.replace("https", "web")
      : imageId.replace("http", "web");

    const pngData = await loadAndCacheImage(imageBaseURL);
    if (!pngData) {
      console.error("Failed to load PNG image:", imageBaseURL);
      return;
    }

    metaData.addProvider(
      (type, imageId) => hardcodedMetaDataProvider(type, imageId, [imageBaseURL]),
      100
    );

    const imageDataMeta = viewport.getImageDataMetadata(pngData);
    if (!imageDataMeta) {
      console.error("Failed to retrieve image metadata:", imageBaseURL);
      return;
    }

    const vtkImageData = createVTKImageData({
      origin: imageDataMeta.origin,
      direction: imageDataMeta.direction,
      dimensions: imageDataMeta.dimensions,
      spacing: loadedImageData.spacing,
      bitsAllocated: 8,
      numberOfComponents: 4,
      numVoxels: imageDataMeta.numVoxels,
    });

    const updatedVtkImageData = updateVTKImageDataRGBA(
      pngData,
      imageDataMeta.imagePlaneModule,
      vtkImageData,
      opacity
    );

    const actor = viewport.createActorMapper(updatedVtkImageData);

    viewport.addActor({ uid, actor, slabThickness: 1 });
    viewport.render();
  };

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {/* Always render SCSeriesPanel on the right side */}
      {(scSeries && scSeries.length > 0) && (
        <SCSeriesPanel
          scSeries={scSeries}
          onSelectSeries={handleSelectSCSeries}
          collapsed={scPanelCollapsed}
          setCollapsed={setScPanelCollapsed}
        />
      )}
      
      {/* Main content */}
      {!findingsAvailable ? (
        <>
          <div className="gizmo-container" style={findingsHeaderStyle}>
               <div style={{
                    //width: '100%', 
                    display: 'flex', flexDirection: 'column', color: '#C4C4C4',
                    fontWeight: 700,
                    fontStyle: 'normal', // 'Bold' is invalid, should be 'normal' or 'italic'
                    fontSize: '12px',
                    letterSpacing: '0px', // '%' not valid here
                    verticalAlign: 'middle' ,
                    width:'150px'}}>
                {patientName}&nbsp;&nbsp;{patientSex}&nbsp;|&nbsp;{patientAge}
              </div>

              <div style={{ 
                //paddingLeft: '5px',
                fontWeight: 700,              // ✅ Bold weight
                fontStyle: 'normal',          // ❌ 'Bold' is invalid, use 'normal' or 'italic'
                fontSize: '10px',
                lineHeight: '25px',
                letterSpacing: '0px', 
                verticalAlign: 'middle',
                paddingLeft: '44px'
               }}>AI Findings: Normal</div>
              <div style={{ display:'flex', marginLeft: 'auto',  cursor: 'pointer', paddingTop: '5px' }} onClick={() => setShowFindings(false)}>
                <DownArrowIcon/>
              </div>
            </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ position: "relative", width: '194px', height: '208px', margin: '0', padding:'0', display: "flex", flexDirection: "row", alignItems: "center" }}>
              {/* {isLoading && (
                <div style={overlayStyle}>
                  <div className="spinner" />
                </div>
              )} */}
              <NearMouseTooltip content={t(`Open Viewer`)}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '194px', height: '208px' }}>
                  <div
                    ref={elementRef}
                    onClick={() => isAugDemo ? handleOpenViewerWindow(augDemoStudyId, augDemoFirmId, augDemoUsername, false,isAugDemo,false,"") :handleOpenViewerWindow(studyId, firmId, userName, false,isAugDemo,false,"")}
                    style={{
                      width: "194px",
                      height: "208px",
                      background: "black",
                      cursor: "pointer",
                    }}
                  />
                  {isLoading && (
                    <div style={viewportOverlayStyle2}>
                      <div className="spinner" />
                    </div>
                  )}

                </div>
              </NearMouseTooltip>
              {imageIds.length > 1 && (
                <ImageScrollbar 
                  height={height} 
                  max={imageIds.length - 1}
                  value={currentImageIndex}
                  onChange={(e) => {
                    const newIndex = parseInt(e.target.value, 10);
                    if (newIndex !== currentImageIndex) {
                      setCurrentImageIndex(newIndex);
                      const renderingEngine = getRenderingEngine(renderingEngineId);
                      const viewport = renderingEngine.getViewport(viewportId);
                      setIsLoading(true);
                      viewport.setStack(imageIds, newIndex).then(() => {
                        viewport.render();
                        setIsLoading(false);
                      });
                    }
                  }}
                > </ImageScrollbar>
              )}

            </div>
          </div>
        </>
      ) : (
        <>
          <div className="gizmo-container" style={findingsHeaderStyle}>
            
            <div style={{display: 'flex', flexDirection: 'column', color: '#C4C4C4',
                  fontWeight: 700,
                  fontStyle: 'normal', // 'Bold' is invalid, should be 'normal' or 'italic'
                  fontSize: '12px',
                  letterSpacing: '0px', // '%' not valid here
                  verticalAlign: 'middle' ,
                  width:'150px'}}>
              {patientName}&nbsp;&nbsp;{patientSex}&nbsp;|&nbsp;{patientAge}
              </div>
          
            <div style={{ 
                // paddingLeft: '50px',
                fontWeight: 700,              // ✅ Bold weight
                fontStyle: 'normal',          // ❌ 'Bold' is invalid, use 'normal' or 'italic'
                fontSize: '10px',
                lineHeight: '25px',
                letterSpacing: '0px', 
                verticalAlign: 'middle',
                paddingLeft: '44px'
             }}>AI Findings: Abnormal</div>
            <div style={{ display:'flex', marginLeft: 'auto',  cursor: 'pointer', paddingTop: '5px'  }} onClick={() => setShowFindings(false)}>
              <DownArrowIcon/>
            </div>
          </div>
          <div className="gizmo-container" style={{ margin: '0px 12px', height: '100%' }}>

            <div style={{ display: 'flex', width: '194px', height: '208px', margin: '0', padding:'0', marginLeft: scSeries && scSeries.length > 0 ? '14px' : '0' }}>

              {/* {isLoading && (
                <div style={overlayStyle}>
                  <div className="spinner" />
                </div>
              )} */}

              <NearMouseTooltip content={t(`Open Viewer`)}>
                <div style={{ position: 'relative', display: 'flex', flexDirection: 'row', alignItems: 'center', width: '194px', height: '208px', margin: '0', padding: '0' }}>
                  <div
                    ref={elementRef}
                    onClick={() => isAugDemo ? handleOpenViewerWindow(augDemoStudyId, augDemoFirmId, augDemoUsername, false,isAugDemo,false,"") :handleOpenViewerWindow(studyId, firmId, userName, false,isAugDemo,false,"")}
                    style={{
                      width: "194px",
                      height: "208px",
                      background: "black",
                      cursor: "pointer",
                    }}
                  />
                  {isLoading && (
                    <div style={viewportOverlayStyle1}>
                      <div className="spinner" />
                    </div>
                  )}
                </div>
              </NearMouseTooltip>
              {imageIds.length > 1 && (
                <ImageScrollbar 
                height={height} 
                max={imageIds.length - 1}
                value={currentImageIndex}
                onChange={(e) => {
                  const newIndex = parseInt(e.target.value, 10);
                  if (newIndex !== currentImageIndex) {
                    setCurrentImageIndex(newIndex);
                    const renderingEngine = getRenderingEngine(renderingEngineId);
                    const viewport = renderingEngine.getViewport(viewportId);
                    setIsLoading(true);
                    viewport.setStack(imageIds, newIndex).then(() => {
                      viewport.render();
                      setIsLoading(false);
                    });
                  }
                }}
              > </ImageScrollbar>
              )}


            </div>
            <div style={{ width: scSeries && scSeries.length > 0 ? '121px' : '135px', marginLeft: 'auto' }}>

              <div className="custom-scroll">
                {processedFindings.map((item, index) => (
                  <div key={index} style={{ ...findingStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {/* Left side: dot + name */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={dotStyle} />
                      <span>{item.pathologyName || item.classificationName}</span>
                    </div>

                    {/* Right side: probability */}
                    {(<span style={{ textAlign: 'right', fontWeight: 300}}>
                      {item.probabilityDisplay != null ? `${item.probabilityDisplay}%` : "— "}
                    </span>
                    )}

                  </div>
                ))}

              </div>
            </div>
          </div>
       </>
      )}
    </div>
  );
};

export default AIAnnotatedImage;
